import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  inject,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { SIDENAV_MODE } from '../sidebar.interface';
import { environment } from '@environments/environment';
import {
  DashboardType,
  SidebarItemsService,
} from 'src/app/shared/services/sidebar-items.service';
import { IBrandingResponse } from '@interfaces/branding/branding.interface';
import { PortalStoreService } from '@stores/portal-store.service';
import { PORTAL_PATH } from '@parameters/routing/portal.parameter';
import { resolvePortalExitUrl } from 'src/app/auth/helpers/portal-landing.helper';
import { merchantPortalNavigationOptionsFromAuth } from 'src/app/auth/helpers/merchant-permissions.helper';
import { AuthenticationService } from 'src/app/auth/service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
export class SidenavComponent implements OnChanges, OnInit {
  private readonly router = inject(Router);
  private readonly portalStore = inject(PortalStoreService);
  private readonly authService = inject(AuthenticationService);
  private readonly commonService = inject(CommonService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdRef = inject(ChangeDetectorRef);

  /**
   * Branding Data
   */
  public brandingData: IBrandingResponse;

  /**
   * Image url
   */
  public readonly imageUrl: string = `${environment.imgFileBaseUrl}/`;

  /* Input property for manage mode of sidebar */
  @Input() public mode: SIDENAV_MODE = 'push';

  /* Input property for manage opening state of sidebar */
  @Input() public opened: boolean = true;

  /* Event emitter if something changed in opened state of sidenav */
  @Output() public toggleSidenav: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  /* Event emitter if something changed in mode state of sidenav */
  @Output() public toggleMode: EventEmitter<SIDENAV_MODE> =
    new EventEmitter<SIDENAV_MODE>();

  /**
   * Constructor
   * @param sidebarItemService
   */
  constructor(private readonly sidebarItemService: SidebarItemsService) {}

  /**
   * On Changes
   */
  ngOnChanges(): void {
    this.handleToggleEvent();
    this.handleModeEvent();
  }

  /**
   * Oninit
   */
  ngOnInit(): void {
    this.brandingData = this.sidebarItemService.brandingData || null;

    this.authService.permissionsUpdated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cdRef.markForCheck();
      });
  }

  /**
   * Branding logo home — leaves Developers/Payments for another allowed page
   * when permissions allow it (updates after refresh-token permission sync).
   */
  public goHome(): void {
    const dashboardType = this.sidebarItemService.activeDashboardType;
    const excludeSegment =
      dashboardType === DashboardType.DEVELOPERS
        ? `/${PORTAL_PATH.developers}`
        : dashboardType === DashboardType.PAYMENT
          ? `/${PORTAL_PATH.payments}`
          : undefined;

    this.sidebarItemService.updateDashboardType(DashboardType.DEFAULT);
    this.commonService.partnerAccountId = '';
    this.commonService.paymentAccountId = '';
    this.commonService.paymentAccessToken = '';
    void this.router.navigateByUrl(
      resolvePortalExitUrl(
        this.portalStore.type,
        this.authService.entityPermissions,
        this.authService.merchantPermissions,
        excludeSegment,
        merchantPortalNavigationOptionsFromAuth(this.authService)
      )
    );
  }

  /* function to hide sidenav */
  public hideSidenav() {
    this.opened = false;
    this.handleToggleEvent();
  }

  /* private function for handle toggle events for opening state */
  private handleToggleEvent() {
    this.toggleSidenav.emit(this.opened);
  }

  /* private function for handle toggle events for mode state */
  private handleModeEvent() {
    this.toggleMode.emit(this.mode);
  }
}
